import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import CertificateSection from '../components/CertificateSection';
import './UserProfile.css';

import { User, Mail, Globe, Building, Phone, GraduationCap, Edit3, Check, X, QrCode, FileText } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const UserProfile: React.FC = () => {
  const [participant, setParticipant] = useState<any>(null);
  const [isGroupMember, setIsGroupMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    country: '',
    institution: '',
    phone: '',
    academicLevel: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // 1. Verificar se é integrante do grupo pelo e-mail
          const qMembers = await getDocs(collection(db, 'integrantes_giepei'));
          const isMember = qMembers.docs.some(doc => doc.data().email?.toLowerCase() === user.email?.toLowerCase());
          setIsGroupMember(isMember);

          // 2. Carregar dados de participante
          const docSnap = await getDoc(doc(db, 'participantes', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setParticipant(data);
            setEditData({
              name: data.name || '',
              country: data.country || '',
              institution: data.institution || '',
              phone: data.phone || '',
              academicLevel: data.academicLevel || 'Estudante'
            });
          }
        } catch (err) {
          console.error("Erro ao carregar perfil:", err);
        }
      } else {
        navigate('/acesso');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleUpdateProfile = async () => {
    if (!editData.name.trim() || !auth.currentUser) return;
    setUpdateLoading(true);
    try {
      await setDoc(doc(db, 'participantes', auth.currentUser.uid), {
        ...editData
      }, { merge: true });
      setParticipant({ ...participant, ...editData });
      setIsEditing(false);
      alert('Dados atualizados com sucesso!');
    } catch (err) {
      alert('Erro ao actualizar dados.');
    }
    setUpdateLoading(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) return <div className="container" style={{padding: '10rem 2rem', textAlign: 'center'}}>Verificando dados...</div>;

  return (
    <div className="user-profile-page container">
      <header className="profile-header">
        <div className="header-title-group">
          <h2>Minha Conta</h2>
          {isGroupMember && <span className="member-badge">Membro GIEPEI</span>}
        </div>
        <div className="header-actions-flex">
          {isGroupMember && (
            <button onClick={() => navigate('/documentos')} className="btn-docs-access">
              <FileText size={18} /> Documentos do Grupo
            </button>
          )}
          <button onClick={handleLogout} className="btn-logout-text">Sair da Conta</button>
        </div>
      </header>

      <div className="profile-grid">
        <div className="profile-main-col">
          <section className="profile-card data-section">
            <div className="card-header">
              <div className="card-title-flex">
                <h3>Meus Dados</h3>
                {!isEditing ? (
                  <button onClick={() => setIsEditing(true)} className="btn-edit-link">
                    <Edit3 size={14} /> Editar Dados
                  </button>
                ) : (
                  <div className="edit-buttons">
                    <button onClick={handleUpdateProfile} disabled={updateLoading} className="btn-save-mini">
                       <Check size={14} /> Salvar
                    </button>
                    <button onClick={() => setIsEditing(false)} className="btn-cancel-link">Cancelar</button>
                  </div>
                )}
              </div>
              <div className="header-line"></div>
            </div>
            
            <div className="info-list">
              <div className="info-group">
                <label>Nome Completo</label> 
                {isEditing ? (
                  <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="form-input" />
                ) : <p>{participant?.name || auth.currentUser?.displayName || 'Não informado'}</p>}
              </div>

              <div className="info-group">
                <label>E-mail</label> 
                {isEditing ? (
                  <input type="email" value={participant?.email || auth.currentUser?.email || ''} disabled className="form-input disabled-field" />
                ) : <p className="text-muted-field">{participant?.email || auth.currentUser?.email}</p>}
              </div>

              <div className="form-row-grid">
                <div className="info-group">
                  <label>País</label> 
                  {isEditing ? (
                    <input type="text" value={editData.country} onChange={(e) => setEditData({...editData, country: e.target.value})} className="form-input" />
                  ) : <p>{participant?.country || 'Não informado'}</p>}
                </div>
                <div className="info-group">
                  <label>Formação</label> 
                  {isEditing ? (
                    <select value={editData.academicLevel} onChange={(e) => setEditData({...editData, academicLevel: e.target.value})} className="form-input">
                      <option value="Estudante">Estudante</option>
                      <option value="Licenciado">Licenciado</option>
                      <option value="Mestre">Mestre</option>
                      <option value="Doctor">Doctor</option>
                    </select>
                  ) : <p>{participant?.academicLevel || 'Não informada'}</p>}
                </div>
              </div>

              <div className="info-group">
                <label>Instituição</label> 
                {isEditing ? (
                  <input type="text" value={editData.institution} onChange={(e) => setEditData({...editData, institution: e.target.value})} className="form-input" />
                ) : <p>{participant?.institution || 'Não informada'}</p>}
              </div>

              <div className="info-group">
                <label>WhatsApp / Telefone</label> 
                {isEditing ? (
                  <input type="text" value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="form-input" />
                ) : <p>{participant?.phone || 'Não informado'}</p>}
              </div>
            </div>
          </section>

          {/* Sección QR solo para modalidad Presencial */}
          {participant?.attendanceMode === 'Presencial' && (
            <section className="profile-card qr-section" style={{ marginTop: '2rem' }}>
              <div className="card-header">
                <div className="title-group-simple">
                  <QrCode size={18} style={{ marginRight: '8px', color: 'var(--secondary-color)' }} />
                  <h3 style={{ fontSize: '1.2rem' }}>Identificação Presencial</h3>
                </div>
                <div className="header-line"></div>
              </div>
              <div className="qr-box-layout">
                <div className="qr-wrapper">
                  <QRCodeCanvas 
                    value={auth.currentUser?.uid || ''} 
                    size={150}
                    level={"H"}
                  />
                </div>
                <div className="qr-text">
                  <p className="qr-status-tag">Inscrição Validada</p>
                  <p>Apresente este código QR na recepção do evento para confirmar sua entrada.</p>
                  <code className="user-id-code">ID: {auth.currentUser?.uid.substring(0, 12)}...</code>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="profile-side-col">
          <section className="profile-card presence-section">
            <div className="card-header">
              <h3>Minha Presença</h3>
              <div className="header-line"></div>
            </div>
            <div className="presence-info">
              <p className="event-name">1º Seminário do GIEPEI (2026)</p>
              <div className="days-list">
                {['dia1', 'dia2', 'dia3'].map((dia, idx) => (
                  <div key={dia} className={`day-item ${participant?.asistencia?.[dia] ? 'check' : ''}`}>
                    <span>Dia {idx + 1}</span>
                    <strong>{participant?.asistencia?.[dia] ? 'Presente' : 'Ausente'}</strong>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <CertificateSection />
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
